import React, { useState, useEffect } from 'react';
import { useWallet } from '../../contexts/WalletContext';
import { getTransactionsForAddress, TRANSACTION_STATUS } from '../../utils/transactionManager';
import { CHAINS } from '../../config/constants';
import { formatAddress, formatTokenAmount, formatTime, formatTxHash } from '../../utils/format';
import LoadingSpinner from '../UI/LoadingSpinner';
import Button from '../UI/Button';
import TransactionRow from './TransactionRow';
import { 
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const TransactionHistory = () => {
  const { address, isConnected } = useWallet();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);

  const itemsPerPage = 20;

  useEffect(() => {
    if (isConnected && address) {
      loadTransactions();
    }
  }, [isConnected, address, currentPage, filter]);

  const loadTransactions = async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      // Get transactions from local storage (no server API)
      const allTransactions = getTransactionsForAddress(address);
      
      let filteredTransactions = allTransactions || [];
      
      // Apply status filter
      if (filter !== 'all') {
        filteredTransactions = filteredTransactions.filter(
          tx => tx.status === filter
        );
      }

      // Apply search filter
      if (searchTerm) {
        filteredTransactions = filteredTransactions.filter(
          tx => 
            tx.txHash.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tx.recipient.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply pagination
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

      setTransactions(paginatedTransactions);
      setTotalPages(Math.ceil(filteredTransactions.length / itemsPerPage));
      setError(null);

    } catch (error) {
      console.error('Failed to load transaction history:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadTransactions(true);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TRANSACTION_STATUS.COMPLETED:
        return 'text-success-600 bg-success-100 dark:bg-success-900/20';
      case TRANSACTION_STATUS.PENDING:
      case TRANSACTION_STATUS.CONFIRMED:
        return 'text-warning-600 bg-warning-100 dark:bg-warning-900/20';
      case TRANSACTION_STATUS.FAILED:
      case TRANSACTION_STATUS.EXPIRED:
        return 'text-error-600 bg-error-100 dark:bg-error-900/20';
      default:
        return 'text-slate-600 bg-slate-100 dark:bg-slate-800';
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: TRANSACTION_STATUS.COMPLETED, label: 'Completed' },
    { value: TRANSACTION_STATUS.PENDING, label: 'Pending' },
    { value: TRANSACTION_STATUS.CONFIRMED, label: 'Confirmed' },
    { value: TRANSACTION_STATUS.FAILED, label: 'Failed' },
    { value: TRANSACTION_STATUS.EXPIRED, label: 'Expired' },
  ];

  if (!isConnected) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-slate-400 mb-4">
          <MagnifyingGlassIcon className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Connect Your Wallet</h3>
        <p className="text-slate-600 dark:text-slate-400">
          Connect your wallet to view your transaction history
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Transaction History (only stored in local browser)
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Track your cross-chain bridge transactions
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={handleRefresh}
            variant="secondary"
            size="sm"
            loading={isRefreshing}
            className="flex items-center gap-2"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by transaction hash or address..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={filter}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="input-field"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
            <span className="ml-3 text-slate-600 dark:text-slate-400">
              Loading transactions...
            </span>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-error-500 mb-4">
              <XCircleIcon className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Error Loading Transactions</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
            <Button onClick={() => loadTransactions()} variant="primary">
              Try Again
            </Button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-slate-400 mb-4">
              <ClockIcon className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Transactions Found</h3>
            <p className="text-slate-600 dark:text-slate-400">
              {searchTerm || filter !== 'all' 
                ? 'No transactions match your current filters'
                : 'You haven\'t made any bridge transactions yet'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-4 p-4 border-b border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-400">
              <div className="col-span-2">Transaction</div>
              <div className="col-span-2">Route</div>
              <div className="col-span-2">Amount</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Time</div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* Transaction Rows */}
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {transactions.map((transaction) => (
                <TransactionRow
                  key={transaction.id || transaction.txHash}
                  transaction={transaction}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="secondary"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory; 